import React, { useEffect, useState } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import TimelineList from "examples/Timeline/TimelineList";
import TimelineItem from "examples/Timeline/TimelineItem";
import { useAuth } from "context/AuthContext";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { useStytchMemberSession } from "@stytch/react/b2b";
import Card from "@mui/material/Card";

interface Comment {
    id: number;
    username: string;
    comment: string;
    timestamp: string;
}

interface CommentsListProps {
    location_id: number;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL

const CommentsList: React.FC<CommentsListProps> = ({ location_id }) => {
    const [comments, setComments] = useState<Comment[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [newComment, setNewComment] = useState<string>("");
    const { sessionToken } = useAuth();
    const { session } = useStytchMemberSession();




    useEffect(() => {
        const fetchComments = async () => {
            try {
                setLoading(true);
                const response = await fetch(
                    `${API_BASE_URL}/db/comments?location_id=${encodeURIComponent(location_id)}`,
                    {
                        headers: {
                            Authorization: `Bearer ${sessionToken}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                setComments(data);
                setError(null);
            } catch (err) {
                console.error("Error fetching comments:", err);
                setError("Failed to load comments");
            } finally {
                setLoading(false);
            }
        };

        fetchComments();
    }, [location_id, sessionToken]);

    const handleDelete = async (commentId: number) => {
        if (window.confirm("Are you sure you want to delete this comment?")) {
            try {
                const response = await fetch(
                    `${API_BASE_URL}/db/comments/${commentId}`,
                    {
                        method: "DELETE",
                        headers: {
                            Authorization: `Bearer ${sessionToken}`,
                            "Content-Type": "application/json",
                        },
                    }
                );
                if (!response.ok) {
                    throw new Error("Failed to delete comment");
                }
                setComments((prevComments) =>
                    prevComments.filter((comment) => comment.id !== commentId)
                );
            } catch (error) {
                console.error("Error deleting comment:", error);
            }
        }
    };

    const handleAddComment = async () => {
        if (!newComment.trim()) return; // do not allow empty comment


        try {
            const response = await fetch(
                `${API_BASE_URL}/db/comments`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${sessionToken}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        location_id,
                        comment: newComment,
                    }),
                }
            );
            if (!response.ok) {
                throw new Error("Error adding comment");
            }
            // Assuming the backend returns the new comment data (with id, username, timestamp)
            const addedComment: Comment = await response.json();
            // Prepend the new comment to the timeline
            setComments((prevComments) => [addedComment, ...prevComments]);
            setNewComment("");
        } catch (error) {
            console.error("Error adding comment:", error);
        }
    };

    if (loading) {
        return (
            <MDTypography variant="button" color="text">
                Loading...
            </MDTypography>
        );
    }

    if (error) {
        return (
            <MDTypography variant="button" color="error">
                {error}
            </MDTypography>
        );
    }

    return (
        <MDBox px={1}>
            <MDBox mt={2} mb={1}>
            <TimelineList title="Comments">
                {comments.map((comment, index) => (
                    <TimelineItem
                        key={comment.id}
                        color="info"
                        icon="chat"
                        title={comment.username}
                        dateTime={new Date(comment.timestamp).toLocaleString()}
                        lastItem={index === comments.length - 1}
                        description={
                            (
                                <MDBox display="flex" alignItems="center" justifyContent="space-between">
                                    <MDTypography variant="body4" color="text">
                                        {comment.comment}
                                    </MDTypography>
                                    <IconButton
                                        onClick={() => handleDelete(comment.id)}
                                        aria-label="delete"
                                        size="small"
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </MDBox>
                            ) as any
                        }
                    />
                ))}
            </TimelineList>
        </MDBox>
            {/* Add Comment Form */}
            <Card>
                <MDBox p={2} mt={2} display="flex" flexDirection="column" gap={1}>
                    <MDTypography variant="h6">
                        Add a new comment
                    </MDTypography>
                    <TextField
                        label="Add a comment"
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        fullWidth
                        variant="outlined"
                        size="small"
                    />
                    <Button
                        onClick={() => {handleAddComment()}}
                        variant="contained"
                        sx={{ width: '200px', color: '#ffffff' }}
                        color="secondary"
                    >
                        Add Comment
                    </Button>
                </MDBox>
            </Card>


        </MDBox>
    );
};

export default CommentsList;
